import { css } from '@emotion/react';
import * as AIUtilities from '@mythos/game/AIUtilities';
import {
  CardAffinity,
  CardType,
  isCardAffinity,
  isCardType,
} from '@mythos/game/CardTypes';
import { makeContextFromGame } from '@mythos/game/Rules';
import invariant from 'invariant';
import { match } from 'ts-pattern';
import { computeCardEffectRows, makeMultiCardRenderer } from './CardView';
import Symbols, {
  CardAffinityToSymbol,
  CardTypeToSymbol,
  SYMBOL_TO_IMAGE,
  SymbolView,
} from './Symbols';

import * as CardAbilities from '@mythos/game/CardAbilities';
import { GamePlayerViewModel, GameViewModel } from '@mythos/game/GameViewModel';
import React, { CSSProperties } from 'react';
import {
  CardAffinityToCardCountBGColor,
  CardTypeToCardCountBGColor,
  UIColorPalette,
} from './ColorPalette';
import HoverCardStore from './HoverCardStore';

const ORDERED_CARD_AFFINITIES = [
  CardAffinity.Tyros,
  CardAffinity.Derion,
  CardAffinity.Zeona,
  CardAffinity.Basic,
];
const ORDERED_CARD_TYPES = [CardType.Unit, CardType.Building, CardType.Event];

export type CardCountTheme = 'default' | 'colored_bg';

export const CardCountView = (props: {
  type: CardAffinity | CardType | 'upgrade';
  count: number;
  upgradeInfo: CardWithUpgradeInfo[];
  prophecyInfo: CardWithProphecyInfo[];
  theme?: CardCountTheme;
  upgradeIndicatorMouseEnterFunction?: () => void;
  upgradeIndicatorMouseLeaveFunction?: () => void;
  prophecyIndicatorMouseEnterFunction?: () => void;
  prophecyIndicatorMouseLeaveFunction?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) => {
  const { type, count } = props;
  const cardCountTheme = props.theme || 'default';
  let { symbol, backgroundColor, filter } = match(type)
    .when(
      (x) => isCardAffinity(x),
      (x) => {
        return {
          symbol: CardAffinityToSymbol[x],
          ...(cardCountTheme === 'colored_bg'
            ? {
                backgroundColor: CardAffinityToCardCountBGColor[x],
                filter: 'brightness(0%) saturate(0%)',
              }
            : {
                backgroundColor: 'rgb(35, 35, 35)',
                filter: '',
              }),
        };
      },
    )
    .when(
      (x) => isCardType(x),
      (x) => {
        return {
          symbol: CardTypeToSymbol[x],
          ...(cardCountTheme === 'colored_bg'
            ? {
                backgroundColor: CardTypeToCardCountBGColor[x],
                filter: 'brightness(0%) saturate(0%)',
              }
            : {
                backgroundColor: 'rgb(35, 35, 35)',
                filter: 'opacity(60%)',
              }),
        };
      },
    )
    .otherwise((x) => invariant(false, 'unknown card type/subtype: %s', x));
  // backgroundColor = 'rgb(35, 35, 35)';
  const container_style: React.CSSProperties = {
    backgroundColor,
  };
  const icon_style: React.CSSProperties = {
    backgroundImage: `url(${SYMBOL_TO_IMAGE[symbol]})`,
    filter: filter,
  };
  const upgrade_indicators = (
    <UpgradeIndicatorGroup
      upgradeInfo={props.upgradeInfo}
      typeCount={count}
      onEnter={props.upgradeIndicatorMouseEnterFunction}
      onLeave={props.upgradeIndicatorMouseLeaveFunction}
    />
  );
  const prophecy_indicators = (
    <ProphecyIndicatorGroup
      prophecyInfo={props.prophecyInfo}
      onEnter={props.prophecyIndicatorMouseEnterFunction}
      onLeave={props.prophecyIndicatorMouseLeaveFunction}
    />
  );
  return (
    <div
      css={CardCountsViewStyles.cardCount}
      title={props.type as string}
      style={container_style}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
    >
      <div css={CardCountsViewStyles.cardCountUnderlay} style={icon_style} />
      <div css={CardCountsViewStyles.cardCountText}>{count}</div>
      <div css={CardCountsViewStyles.upgradeIndicatorContainer}>
        {upgrade_indicators}
      </div>
      <div css={CardCountsViewStyles.prophecyIndicatorContainer}>
        {prophecy_indicators}
      </div>
    </div>
  );
};
export const CardCountsView = (props: {
  player: GamePlayerViewModel;
  game: GameViewModel;
  singleRow?: boolean;
  className?: string;
  style?: React.CSSProperties;
  theme?: CardCountTheme;
  hideUpgradeIndicators?: boolean;
  hideProphecyIndicators?: boolean;
  onClick?: () => void;
}) => {
  const { player, game } = props;

  const context = makeContextFromGame(player, game);
  const card_counts = AIUtilities.cardCompositionCounts(player);

  const pending_upgrade_info_by_type = ORDERED_CARD_TYPES.reduce(
    (acc, type) => {
      acc[type] = [];
      return acc;
    },
    {} as Record<CardType, CardWithUpgradeInfo[]>,
  );
  const pending_upgrade_info_by_affinity = ORDERED_CARD_AFFINITIES.reduce(
    (acc, affinity) => {
      acc[affinity] = [];
      return acc;
    },
    {} as Record<CardAffinity, CardWithUpgradeInfo[]>,
  );
  const prophecy_info_by_type = ORDERED_CARD_TYPES.reduce(
    (acc, type) => {
      acc[type] = [];
      return acc;
    },
    {} as Record<CardType, CardWithProphecyInfo[]>,
  );
  const prophecy_info_by_affinity = ORDERED_CARD_AFFINITIES.reduce(
    (acc, affinity) => {
      acc[affinity] = [];
      return acc;
    },
    {} as Record<CardAffinity, CardWithProphecyInfo[]>,
  );
  player.cards.forEach((card) => {
    if (!props.hideProphecyIndicators) {
      const prophecy_ability = CardAbilities.getEndOfGameCardAbility(card);
      if (prophecy_ability) {
        for (const output of prophecy_ability.outputs) {
          if (output.multiplier) {
            const synergyInfo = {
              cardID: card.id,
              multiplier: output.outputRatio || 1,
            };
            match(output.multiplier.resource)
              .when(isCardAffinity, (affinity) => {
                prophecy_info_by_affinity[affinity].push(synergyInfo);
              })
              .when(isCardType, (type) => {
                prophecy_info_by_type[type].push(synergyInfo);
              })
              .otherwise(() => {});
          }
        }
      }
    }
    if (!props.hideUpgradeIndicators) {
      const upgrade_condition = computeCardEffectRows(card).upgradeCondition;
      if (
        upgrade_condition &&
        upgrade_condition.upgradeAmount >
          card_counts[upgrade_condition.upgradeType]
      ) {
        const synergyInfo = {
          cardID: card.id,
          threshold: upgrade_condition.upgradeAmount,
        };
        match(upgrade_condition.upgradeType)
          .when(isCardAffinity, (affinity) => {
            pending_upgrade_info_by_affinity[affinity].push(synergyInfo);
          })
          .when(isCardType, (type) => {
            pending_upgrade_info_by_type[type].push(synergyInfo);
          })
          .otherwise(() => {});
      }
    }
  });

  const indicatorHoverFunction = (cardIDs: string[]) => {
    HoverCardStore.setCard(cardIDs, makeMultiCardRenderer(game.cardsByID));
    HoverCardStore.setPosition('top-right-offset');
  };

  const affinity_cards = ORDERED_CARD_AFFINITIES.map((affinity) => {
    if (affinity === CardAffinity.Basic && props.singleRow) {
      return;
    }
    return (
      <CardCountView
        key={affinity}
        type={affinity}
        count={card_counts[affinity] || 0}
        upgradeInfo={pending_upgrade_info_by_affinity[affinity]}
        prophecyInfo={prophecy_info_by_affinity[affinity]}
        theme={props.theme}
        upgradeIndicatorMouseEnterFunction={() => {
          indicatorHoverFunction(
            pending_upgrade_info_by_affinity[affinity].map(
              (info) => info.cardID,
            ),
          );
        }}
        upgradeIndicatorMouseLeaveFunction={() => {
          HoverCardStore.setCard(null, null);
        }}
        prophecyIndicatorMouseEnterFunction={() => {
          indicatorHoverFunction(
            prophecy_info_by_affinity[affinity].map((info) => info.cardID),
          );
        }}
        prophecyIndicatorMouseLeaveFunction={() => {
          HoverCardStore.setCard(null, null);
        }}
      />
    );
  });
  const type_cards = ORDERED_CARD_TYPES.map((type) => {
    return (
      <CardCountView
        key={type}
        type={type}
        count={card_counts[type] || 0}
        upgradeInfo={pending_upgrade_info_by_type[type]}
        prophecyInfo={prophecy_info_by_type[type]}
        theme={props.theme}
        upgradeIndicatorMouseEnterFunction={() => {
          indicatorHoverFunction(
            pending_upgrade_info_by_type[type].map((info) => info.cardID),
          );
        }}
        upgradeIndicatorMouseLeaveFunction={() => {
          HoverCardStore.setCard(null, null);
        }}
        prophecyIndicatorMouseEnterFunction={() => {
          indicatorHoverFunction(
            prophecy_info_by_type[type].map((info) => info.cardID),
          );
        }}
        prophecyIndicatorMouseLeaveFunction={() => {
          HoverCardStore.setCard(null, null);
        }}
      />
    );
  });
  return (
    <div
      css={CardCountsViewStyles.cardCountsContainer}
      className={props.className}
      style={props.style}
      onClick={props.onClick}
    >
      {props.singleRow ? (
        <div css={CardCountsViewStyles.cardCountsRow}>
          {affinity_cards}
          {type_cards}
        </div>
      ) : (
        <>
          <div css={CardCountsViewStyles.cardCountsRow}>{affinity_cards}</div>
          <div css={CardCountsViewStyles.cardCountsRow}>{type_cards}</div>
        </>
      )}
    </div>
  );
};

export type CardWithProphecyInfo = {
  cardID: string;
  multiplier: number;
};
export type CardWithUpgradeInfo = {
  cardID: string;
  threshold: number;
};

const ProphecyIndicator = (props: {
  style: CSSProperties;
  onEnterIndicator?: () => void;
  onLeaveIndicator?: () => void;
}) => {
  return (
    <div
      css={CardCountsViewStyles.prophecyIndicator}
      style={props.style}
      onMouseEnter={props.onEnterIndicator}
      onMouseLeave={props.onLeaveIndicator}
    />
  );
};

const ProphecyIndicatorGroup = (props: {
  prophecyInfo: CardWithProphecyInfo[];
  onEnter?: () => void;
  onLeave?: () => void;
}) => {
  const totalProphecyValue = props.prophecyInfo.reduce(
    (acc, info) => acc + info.multiplier,
    0,
  );
  let indicators = [];
  const indicator_coord_length = 3;
  const indicator_coords = [
    [indicator_coord_length, 0],
    [0, -indicator_coord_length],
    [-indicator_coord_length, 0],
    [0, indicator_coord_length],
    [0, 0],
  ];
  for (let i = 0; i < Math.min(totalProphecyValue, 5); i++) {
    indicators.push(
      <ProphecyIndicator
        key={i}
        style={{
          left: indicator_coords[i][0] - 1,
          top: indicator_coords[i][1] + 1,
        }}
        onEnterIndicator={props.onEnter}
        onLeaveIndicator={props.onLeave}
      />,
    );
  }
  return indicators;
};

const UpgradeIndicatorGroup = (props: {
  upgradeInfo: CardWithUpgradeInfo[];
  typeCount: number;
  onEnter?: () => void;
  onLeave?: () => void;
}) => {
  return props.upgradeInfo.length > 0 ? (
    <div onMouseEnter={props.onEnter} onMouseLeave={props.onLeave}>
      <SymbolView
        key="upgrade-indicator"
        css={CardCountsViewStyles.upgradeIndicator}
        symbol={Symbols.UPGRADE}
      />
    </div>
  ) : null;
};

const CardCountsViewStyles = {
  cardCount: css({
    position: 'relative',
    boxSizing: 'border-box',
    display: 'inline-flex',
    borderRadius: 5,
    height: 22,
    width: 17,
    alignItems: 'center',
    justifyContent: 'center',
  }),
  cardCountUnderlay: css({
    position: 'absolute',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    height: '100%',
    width: '100%',
  }),
  cardCountText: css({
    fontSize: 13,
    position: 'absolute',
    fontWeight: 'bold',
    color: UIColorPalette.cardCountText,
    textShadow: `0 0 5px rgb(0, 0, 0)`,
    // backgroundColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: 3,
    padding: 2,
    background: `radial-gradient(
      ellipse at center,
      rgba(0, 0, 0, 0.3) 0%,
      rgba(0, 0, 0, 0.2) 30%,
      rgba(0, 0, 0, 0.1) 60%,
      transparent 100%
    )`,
  }),
  cardCountsContainer: css({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'stretch',
    flexShrink: 0,
    gap: 2,
  }),
  cardCountsRow: css({
    display: 'flex',
    flexDirection: 'row',
    gap: 3,
  }),
  upgradeIndicatorContainer: css({
    position: 'absolute',
    bottom: -1,
    right: -2,
  }),
  prophecyIndicatorContainer: css({
    position: 'absolute',
    top: 0.5,
    right: 5,
  }),
  prophecyIndicator: css({
    position: 'absolute',
    border: `0.5px solid ${UIColorPalette.cardCountProphecyBorder}`,
    transform: 'rotate(45deg)',
    height: 3,
    width: 3,
    backgroundColor: UIColorPalette.cardCountProphecy,
  }),
  upgradeIndicator: css({
    height: 9,
    width: 9,
  }),
};
